import { DragDropContext, OnDragEndResponder } from '@hello-pangea/dnd';
import { Grid } from '@mui/material';
import isEqual from 'lodash/isEqual';
import { useEffect, useState } from 'react';
import { useListContext, useNotify } from 'react-admin';
import { MissionLight, MissionStatus, setMissionStatus } from '@wwf/sdk';
import { MissionColumn } from './MissionColumn';
import { MissionByStatus, getMissionByStatus } from './getMissionByStatus';
import { useConfigurationContext } from '@app/root/ConfigurationContext';
import { MissionEmpty } from '../list/MissionEmpty';

export const MissionGridContent = () => {
    const { missionStatusLabels } = useConfigurationContext();
    const missionStatusLabelsWithoutArchivedAndCancelled =
        missionStatusLabels.filter(
            status =>
                status.value !== 'ARCHIVED' && status.value !== 'CANCELLED'
        );

    const { data: unorderedmission, isPending } =
        useListContext<MissionLight>();
    const notify = useNotify();

    const [missionByStatus, setMissionByStatus] = useState<MissionByStatus>(
        getMissionByStatus([], missionStatusLabelsWithoutArchivedAndCancelled)
    );

    useEffect(() => {
        if (unorderedmission) {
            const newMissionByStatus = getMissionByStatus(
                unorderedmission,
                missionStatusLabelsWithoutArchivedAndCancelled
            );
            if (!isEqual(newMissionByStatus, missionByStatus)) {
                setMissionByStatus(newMissionByStatus);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unorderedmission]);

    if (isPending) return null;

    const onDragEnd: OnDragEndResponder = result => {
        const { destination, source } = result;

        if (!destination) {
            return;
        }

        if (
            destination.droppableId === source.droppableId &&
            destination.index === source.index
        ) {
            return;
        }

        const sourceStatus = source.droppableId as MissionStatus;
        const destinationStatus = destination.droppableId as MissionStatus;
        const sourceMission = missionByStatus[sourceStatus][source.index]!;

        // compute local state change synchronously
        setMissionByStatus(
            updateMissionStatusLocal(
                sourceMission,
                sourceStatus,
                destinationStatus,
                missionByStatus
            )
        );

        setMissionStatus(sourceMission.id, { status: destinationStatus })
            .then(() => {
                notify('Mission status updated', { type: 'info' });
            })
            .catch(error => {
                console.error(error);
                setMissionByStatus(prev =>
                    revertMissionStatusLocal(
                        sourceMission.id,
                        sourceStatus,
                        destinationStatus,
                        source.index,
                        prev
                    )
                );
                notify(
                    error instanceof Error
                        ? error.message
                        : 'Could not update mission status',
                    { type: 'error' }
                );
            });
    };

    if (!unorderedmission?.length) return <MissionEmpty />;

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Grid container>
                {missionStatusLabelsWithoutArchivedAndCancelled.map(status => (
                    <Grid
                        item
                        md={
                            12 /
                            missionStatusLabelsWithoutArchivedAndCancelled.length
                        }
                    >
                        <MissionColumn
                            status={status.value}
                            missionList={missionByStatus[status.value]}
                            key={status.value}
                        />
                    </Grid>
                ))}
            </Grid>
        </DragDropContext>
    );
};

const updateMissionStatusLocal = (
    sourceMission: MissionLight,
    sourceStatus: MissionStatus,
    destinationStatus: MissionStatus,
    missionByStatus: MissionByStatus
) => {
    // Remove mission from source column
    const sourceColumn = missionByStatus[sourceStatus];
    const updatedSourceColumn = sourceColumn.filter(
        mission => mission.id !== sourceMission.id
    );

    // Add mission to destination column
    const destinationColumn = missionByStatus[destinationStatus];
    const updatedDestinationColumn = [
        ...destinationColumn,
        { ...sourceMission, status: destinationStatus },
    ];

    return {
        ...missionByStatus,
        [sourceStatus]: updatedSourceColumn,
        [destinationStatus]: updatedDestinationColumn,
    };
};

const revertMissionStatusLocal = (
    missionId: MissionLight['id'],
    sourceStatus: MissionStatus,
    destinationStatus: MissionStatus,
    sourceIndex: number,
    missionByStatus: MissionByStatus
): MissionByStatus => {
    const destinationColumn = missionByStatus[destinationStatus];
    const mission = destinationColumn.find(m => m.id === missionId);
    if (!mission) {
        return missionByStatus;
    }
    const updatedDestinationColumn = destinationColumn.filter(
        m => m.id !== missionId
    );
    const sourceColumn = missionByStatus[sourceStatus];
    const revertedMission: MissionLight = {
        ...mission,
        status: sourceStatus,
    };
    const updatedSourceColumn = [
        ...sourceColumn.slice(0, sourceIndex),
        revertedMission,
        ...sourceColumn.slice(sourceIndex),
    ];

    return {
        ...missionByStatus,
        [sourceStatus]: updatedSourceColumn,
        [destinationStatus]: updatedDestinationColumn,
    };
};
